import React, { useState, useEffect, useCallback } from 'react';
import { Box, Flex, VStack, HStack, Text, Avatar, Icon, Spinner, Button } from '@chakra-ui/react';
import { FiExternalLink, FiAlertCircle, FiRefreshCw, FiCreditCard, FiMail, FiPhone, FiBookOpen } from 'react-icons/fi';
import { getCustomer } from 'services/woo';
import { getRespondentSurveys } from 'services/surveyService';
import { codeToDisplayLabel, normalizeCourseName } from 'utils/courseUtils';
import { AVATAR_GRADIENTS, getInitials } from 'utils/avatarStyles';
import AntecedentesTab from 'views/Dashboard/Profile/components/AntecedentesTab';

const openProfile = (wpUserId) => {
  if (!wpUserId) return;
  window.open(`${window.location.origin}/#/admin/usuario/${wpUserId}`, '_blank');
};

// Badge chico con ícono — RUT/email/teléfono se leen mucho más rápido "de
// un vistazo" como esto que como texto suelto separado por comas.
const MetaBadge = ({ icon, children }) => (
  <HStack spacing="5px" px="10px" py="4px" borderRadius="999px" bg="cream.bg2" flexShrink={0} maxW="100%">
    <Icon as={icon} boxSize="12px" color="ink.400" flexShrink={0} />
    <Text fontSize={{ base: '12px', md: '12.5px' }} color="ink.700" fontWeight="600" noOfLines={1}>{children}</Text>
  </HStack>
);

// Cabecera de solo lectura — avatar, nombre, RUT/email/teléfono (como
// badges con ícono, para leerlos de un vistazo) y curso. Sin el chip de
// estado (Activo/Becado/etc.) — no aporta acá, es ruido para lo que importa
// en una Consulta rápida de portería. Mismo cálculo que ModernUserHeader en
// Profile/index.js (misma fuente de verdad visual), pero sin edición de
// avatar ni botones de acción: acá es un vistazo rápido durante una
// Consulta, no la ficha editable completa.
const ConsultaHeader = ({ customer }) => {
  const fullName = `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Sin nombre';
  const group = customer.meta_data?.find(m => m.key === 'group_name')?.value;
  const groupLabel = group ? codeToDisplayLabel(normalizeCourseName(group)) : null;
  const hasAvatarMeta = customer.meta_data?.some(m => m.key === 'avatar_url' && m.value);
  const avatarSrc = hasAvatarMeta
    ? `${process.env.REACT_APP_SURVEY_API_URL}/api/users/${customer.username}/avatar-image`
    : null;
  const initials = getInitials(fullName);
  const avatarGradient = AVATAR_GRADIENTS[(customer.id || 0) % AVATAR_GRADIENTS.length];
  const phone = customer.meta_data?.find(m => m.key === 'apoderado_telefono')?.value
    || customer.meta_data?.find(m => m.key === 'telefono')?.value;

  return (
    <Flex align="center" gap={{ base: 3, md: 4 }} px={{ base: 4, md: 6 }} pt={{ base: 3, md: 4 }} pb={{ base: 3, md: 4 }}
      borderBottom="1px solid" borderColor="cream.line">
      <Box as="button" type="button" position="relative" flexShrink={0}
        onClick={() => openProfile(customer.id)} title="Abrir ficha del alumno">
        {avatarSrc ? (
          <Avatar src={avatarSrc} name={fullName} w={{ base: '56px', md: '64px' }} h={{ base: '56px', md: '64px' }} borderRadius="14px" />
        ) : (
          <Flex w={{ base: '56px', md: '64px' }} h={{ base: '56px', md: '64px' }} borderRadius="14px"
            bgGradient={avatarGradient} align="center" justify="center"
            fontWeight="800" color="white" fontSize={{ base: '19px', md: '22px' }}>
            {initials}
          </Flex>
        )}
      </Box>
      <Box flex={1} minW={0}>
        <HStack spacing="6px" as="button" type="button" onClick={() => openProfile(customer.id)} role="group">
          <Text fontSize={{ base: '16px', md: '19px' }} fontWeight="800" color="ink.900" noOfLines={1}
            _groupHover={{ textDecoration: 'underline' }}>
            {fullName}
          </Text>
          <Icon as={FiExternalLink} boxSize="13px" color="ink.400" flexShrink={0} />
        </HStack>
        <HStack mt="7px" spacing="6px" flexWrap="wrap">
          <MetaBadge icon={FiCreditCard}>{customer.username}</MetaBadge>
          {customer.email && <MetaBadge icon={FiMail}>{customer.email}</MetaBadge>}
          {phone && <MetaBadge icon={FiPhone}>{phone}</MetaBadge>}
          {groupLabel && (
            <HStack spacing="5px" px="10px" py="4px" borderRadius="999px" bg="sky.bg" flexShrink={0}>
              <Icon as={FiBookOpen} boxSize="12px" color="sky.ink" flexShrink={0} />
              <Text fontSize={{ base: '12px', md: '12.5px' }} color="sky.ink" fontWeight="700" noOfLines={1}>{groupLabel}</Text>
            </HStack>
          )}
        </HStack>
      </Box>
    </Flex>
  );
};

// Cuerpo de la tarjeta de Consulta — reemplaza la lista corta de campos que
// había antes por EXACTAMENTE el tab "Antecedentes" de la ficha del alumno
// (mismo componente, ver Profile/components/AntecedentesTab.js), precedido
// por una cabecera de solo lectura (ConsultaHeader arriba). A diferencia de
// `student` (payload liviano de validate()), acá se trae el customer
// COMPLETO vía getCustomer() — Antecedentes necesita customer.id (para
// poder guardar ediciones), customer.role y customer.meta_data completo,
// que validate() no garantiza.
const ConsultaResult = ({ student }) => {
  const [customer, setCustomer]         = useState(null);
  const [userSurveys, setUserSurveys]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [loadError, setLoadError]       = useState(null);

  const fetchAll = useCallback(async () => {
    if (!student?.wp_user_id) {
      setLoadError('Este registro no tiene una ficha de alumno asociada.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const [custRes, surveysRes] = await Promise.all([
        getCustomer(student.wp_user_id),
        getRespondentSurveys(student.rut ?? student.username).catch(() => ({ data: [] })),
      ]);
      if (custRes?.data) {
        setCustomer(custRes.data);
        setUserSurveys(surveysRes?.data || surveysRes || []);
      } else {
        setLoadError('No se pudo cargar la ficha del alumno.');
      }
    } catch (e) {
      setLoadError('No se pudo cargar la ficha del alumno.');
    } finally {
      setLoading(false);
    }
  }, [student]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) {
    return (
      <Flex align="center" justify="center" py="48px">
        <VStack spacing={3}>
          <Spinner color="coral.500" size="lg" />
          <Text fontSize="13.5px" color="ink.400" fontWeight="600">Cargando antecedentes…</Text>
        </VStack>
      </Flex>
    );
  }

  if (loadError || !customer) {
    return (
      <VStack spacing={3} py="40px" px={{ base: 4, md: 6 }}>
        <Icon as={FiAlertCircle} color="danger.500" boxSize={7} />
        <Text fontSize="14px" color="ink.700" fontWeight="600" textAlign="center">{loadError}</Text>
        <Button size="sm" variant="outline" borderColor="cream.line" leftIcon={<Icon as={FiRefreshCw} />} onClick={fetchAll}>
          Reintentar
        </Button>
      </VStack>
    );
  }

  return (
    <Box>
      <ConsultaHeader customer={customer} />
      <AntecedentesTab customer={customer} onUpdate={fetchAll} hasAgenda={false} userSurveys={userSurveys} />
    </Box>
  );
};

export default ConsultaResult;
